import { GridApi } from "ag-grid-community";
import { Button, Modal } from "antd";
import React, { useEffect, useState } from "react";
import AddToExact from "./AddToExact";
import { useDispatch, useSelector } from "react-redux";
import {
    ColorHex,
    Keyword,
    TActiveClientId,
    TKeywordsSelectionData,
} from "@/ts/common-types";
import { createDatasource } from "@/ts/retrieve-data";
import { getDateObj } from "@/ts/date-range";
import * as Sentry from "@sentry/react";
import { map } from "underscore";
import {
    useGetBulkActionBtnConfig,
    useGetModulesData,
} from "@/hooks/AllCustomHooks";
import { setAddAsExactData } from "@/features/modalSlice";
import { handleAddExactBtnClick } from "@/ts/utils";
import ExportGridBtn from "@/components/buttons/ExportGridBtn";

interface IAddToExactProps {
    show: boolean;
    setShow: (show: boolean) => void;
    gridApi: GridApi;
}

const AddToExactModal: React.FC<IAddToExactProps> = ({ show, setShow }) => {
    const dispatch = useDispatch();

    const [loading, setLoading] = useState<boolean>(true);
    const [rowData, setRowData] = useState<Keyword[]>();
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const keywordsSelectionData = useSelector(
        (state: TKeywordsSelectionData) => state.keywordsSelectionData.value
    );
    const addAsExactData = useSelector(
        (state: TKeywordsSelectionData) => state.addAsExactData.value
    );

    const selectedKeywords = map(
        keywordsSelectionData,
        (data: Keyword) => data.keyword_name
    );

    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );

    const [addToExactGridApi, setAddToExactGridApi] = useState<GridApi>();

    const addAsExactConfig = useGetBulkActionBtnConfig();
    const exactLoader = addAsExactConfig["exact"].loader;
    const negativeLoader = addAsExactConfig["negativeExact"].loader;

    const gridParams = useGetModulesData({
        type: "grid-params",
        subGridType: "addAsExact",
    });

    const onAddToExactSelectionChanged = () => {
        const selectedNodes = map(
            addToExactGridApi.getSelectedNodes(),
            (node) => node.data
        );
        if (selectedNodes.length) {
            dispatch(setAddAsExactData(selectedNodes));
        } else {
            dispatch(setAddAsExactData([]));
        }
        setIsDisabled(!selectedNodes.length);
    };

    const populateAddToExactGrid = async () => {
        try {
            const data = await createDatasource("smart-keywords", {
                ...(selectedKeywords.length && { keywords: selectedKeywords }),
                dateObj: getDateObj(),
            });
            return data;
        } catch (err) {
            console.error(err);
        }
    };

    const ModalHeaderElement = () => (
        <div
            className="d-flex align-items-center pb-3 me-3"
            style={{ fontSize: "16px" }}
        >
            <div>Add As Exact</div>
            <ExportGridBtn
                isSubModule={true}
                onClickFunction={() => addToExactGridApi?.exportDataAsExcel()}
            />
        </div>
    );

    const handleClose = () => {
        setShow(!show);
        dispatch(setAddAsExactData([]));
    };

    useEffect(() => {
        if (show) {
            setLoading(true);
            populateAddToExactGrid()
                .then((smartKeywords) => {
                    setRowData(smartKeywords as Array<Keyword>);
                    setLoading(false);
                })
                .catch((err) => {
                    console.error(err);
                    Sentry.captureException(err);
                });
        }
    }, [activeClientId, show]);

    useEffect(() => {
        const initialFilters = {
            roas: { filterType: "number", type: "greaterThan", filter: 1 },
            spend: {
                filterType: "number",
                type: "greaterThan",
                filter: 100,
            },
        };

        if (addToExactGridApi) {
            addToExactGridApi.setFilterModel(initialFilters);
        }
    }, [addToExactGridApi]);

    return (
        <Modal
            open={show}
            title={<ModalHeaderElement />}
            style={{ top: 20 }}
            footer={[
                <Button
                    disabled={isDisabled}
                    loading={exactLoader}
                    type="primary"
                    style={{
                        background: !isDisabled
                            ? ColorHex.SUCCESS
                            : ColorHex.WHITE,
                    }}
                    id="review-selection-grid-btn"
                    className="me-2"
                    onClick={() =>
                        handleAddExactBtnClick({
                            matchType: "exact",
                            addAsExactData,
                            addAsExactConfig,
                            bidField: gridParams.bidColumn,
                            show,
                            setShow,
                        })
                    }
                >
                    Add as Exact
                </Button>,

                <Button
                    disabled={isDisabled}
                    loading={negativeLoader}
                    className="me-2"
                    id="keyword-add-btn"
                    type="primary"
                    style={{
                        background: !isDisabled
                            ? ColorHex.DANGER
                            : ColorHex.WHITE,
                    }}
                    onClick={() =>
                        handleAddExactBtnClick({
                            matchType: "negativeExact",
                            addAsExactData,
                            addAsExactConfig,
                            show,
                            setShow,
                        })
                    }
                >
                    Add as Negative Exact
                </Button>,
                <Button
                    style={{
                        background: ColorHex.WHITE,
                    }}
                    data-bs-dismiss="modal"
                    onClick={handleClose}
                >
                    Close
                </Button>,
            ]}
            onOk={handleClose}
            onCancel={handleClose}
            width="95vw"
            styles={{
                body: {
                    height: "72vh",
                    overflowY: "auto",
                },
            }}
        >
            <div style={{ height: "100%" }}>
                <AddToExact
                    rowData={rowData}
                    setAddToExactGridApi={setAddToExactGridApi}
                    loading={loading}
                    onAddToExactSelectionChanged={onAddToExactSelectionChanged}
                />
            </div>
        </Modal>
    );
};

export default AddToExactModal;
